/**
 * FeedbackPopup — shown on the Ticket Creator dashboard 3 seconds after a
 * ticket transitions to "Resolved". Requires the user to mark satisfied or
 * not-satisfied; if not-satisfied, a reason (>=10 chars) is mandatory.
 *
 * Behaviour summary:
 *   - Auto-opens once per eligible ticket (parent component controls `open`).
 *   - Displays the 24-hour feedback window notice at the top.
 *   - Cannot be dismissed by pressing escape / clicking outside — the user
 *     either submits feedback or closes explicitly (Later). If the 24h
 *     window elapses, the backend scheduler auto-closes it and the popup
 *     stops appearing.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ThumbsUp, ThumbsDown, Clock, CheckCircle } from 'lucide-react';
import { ticketAPI } from '@/lib/api';

const FeedbackPopup = ({ open, onClose, onSubmitted, ticket }) => {
  const [choice, setChoice] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setChoice(null);
      setReason('');
    }
  }, [open, ticket?.ticket_id]);

  if (!ticket) return null;

  const handleSubmit = async () => {
    if (choice === null) {
      toast.error('Please mark Satisfied or Not Satisfied');
      return;
    }
    if (choice === false) {
      if (!reason.trim()) {
        toast.error('Please mention the reason for not being satisfied');
        return;
      }
      if (reason.trim().length < 10) {
        toast.error('Reason must be at least 10 characters');
        return;
      }
    }
    setSubmitting(true);
    try {
      await ticketAPI.submitFeedback(ticket.ticket_id, {
        is_satisfactory: choice === true,
        reason: choice === false ? reason.trim() : null,
      });
      toast.success('Thank you! Your feedback has been recorded.');
      onSubmitted?.(ticket.ticket_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="sm:max-w-[520px]"
        data-testid="feedback-popup"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-[#1e3a5f] flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Ticket #{ticket.ticket_id} Resolved — Share Your Feedback
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {ticket.department || ''}{ticket.department ? ' · ' : ''}
            Resolved by {ticket.assigned_staff_name || 'your resolver'}.
          </DialogDescription>
        </DialogHeader>

        <div
          className="bg-amber-50 border-l-4 border-amber-500 rounded-md p-3 my-2"
          data-testid="feedback-window-note"
        >
          <div className="flex items-start gap-2 text-xs sm:text-sm text-amber-900">
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              Feedback can only be submitted within <strong>24 hours</strong> of the ticket
              being marked Resolved. After that, this section is automatically deleted.
            </span>
          </div>
        </div>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-medium">How was your experience? *</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <button
                type="button"
                data-testid="feedback-satisfied-btn"
                onClick={() => setChoice(true)}
                className={`flex flex-col items-center gap-2 border-2 rounded-lg p-4 transition-all ${
                  choice === true
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-slate-200 hover:border-emerald-300'
                }`}
              >
                <ThumbsUp className={`w-7 h-7 ${choice === true ? 'text-emerald-600' : 'text-slate-500'}`} />
                <span className={`text-sm font-medium ${choice === true ? 'text-emerald-700' : 'text-slate-700'}`}>
                  Satisfied
                </span>
              </button>
              <button
                type="button"
                data-testid="feedback-not-satisfied-btn"
                onClick={() => setChoice(false)}
                className={`flex flex-col items-center gap-2 border-2 rounded-lg p-4 transition-all ${
                  choice === false
                    ? 'border-red-500 bg-red-50'
                    : 'border-slate-200 hover:border-red-300'
                }`}
              >
                <ThumbsDown className={`w-7 h-7 ${choice === false ? 'text-red-600' : 'text-slate-500'}`} />
                <span className={`text-sm font-medium ${choice === false ? 'text-red-700' : 'text-slate-700'}`}>
                  Not Satisfied
                </span>
              </button>
            </div>
          </div>

          {choice === false && (
            <div className="space-y-2">
              <Label htmlFor="feedback-reason" className="text-sm font-medium">
                Please mention the reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="feedback-reason"
                data-testid="feedback-reason-textarea"
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Tell us what went wrong so the admin team can review (minimum 10 characters)"
              />
              <p className="text-xs text-slate-500">{reason.length} characters</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="feedback-later-btn">
            Later
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || choice === null}
            data-testid="feedback-submit-btn"
            className={
              choice === true
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : choice === false
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-slate-400'
            }
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackPopup;
